/**
 * One group-summary cell, drawn identically by the group **footer** and by a **collapsed**
 * group's summary strip (parity cards P-027, P-030).
 *
 * The two used to be different things — the footer rendered cells, a collapsed group rendered
 * nothing — and monday draws the same cell in both places, so it lives here once. The only
 * difference is the label: a collapsed group has no column-header row above it, so each cell
 * that has a summary carries its column's title
 * (`discovery/ui-reference/parity/90397660/group-collapsed-monday.png`, the `CUS` / `DX`
 * batteries at y 213-222 with the bar at y 235-258).
 *
 * A column with **no** summary renders an empty cell, which is monday's default: on
 * `90397660` the `mirror` and `person` cells are blank where ours printed `0` (P-027).
 */
import type { ColumnId } from '@elizmonday/shared';
import { getColumnUi } from '../columns/registry';
import type { BoardColumn, BoardGroupView } from '../data/types';
import { AGGREGATION_LABELS, defaultAggregation, footerHidden } from './summaries';

export interface SummaryCellProps {
  group: BoardGroupView;
  column: BoardColumn;
  style: React.CSSProperties;
  /** Collapsed strip: print the column title over the value, as monday does. */
  labelled?: boolean;
  onOpenSummaryMenu?(columnId: ColumnId, x: number, y: number): void;
}

export function SummaryCell({ group, column, style, labelled = false, onOpenSummaryMenu }: SummaryCellProps) {
  if (footerHidden(column)) return <span role="gridcell" style={style} />;
  const summary = group.summaries[column.id];
  const ui = getColumnUi(column.type);
  return (
    <button
      type="button"
      role="gridcell"
      className="ez-footer__cell"
      data-labelled={labelled || undefined}
      style={style}
      data-testid={`summary-${group.id}-${column.id}`}
      title={AGGREGATION_LABELS[defaultAggregation(column)] ?? ''}
      onClick={(event) => {
        const box = event.currentTarget.getBoundingClientRect();
        onOpenSummaryMenu?.(column.id, box.left, box.top);
      }}
    >
      {labelled && summary ? <span className="ez-footer__label">{column.title}</span> : null}
      {summary ? (
        ui.Summary ? (
          <ui.Summary summary={summary} column={column} />
        ) : (
          <span className="ez-footer__value">{summary.text}</span>
        )
      ) : (
        <span className="ez-footer__placeholder" aria-hidden="true" />
      )}
    </button>
  );
}
